// Central catalog configuration for /products page
// - Categories define the sections and slugs used for hash links
// - productsByCategory is placeholder data to render cards now
// - IMPORTANT: CatalogProduct.id is the InkSoft *Product ID* used by the
//   embedded designer, not the SKU or style code. If this id is wrong,
//   InkSoft will open the generic home screen instead of the garment.
export type Category = {
	name: string;
	slug: string;
	description?: string;
	// Optional nested InkSoft category path such as "Ampbrand.com > Hats".
	inksoftCategoryPath?: string;
};

// Small helper shape for optional color variants on catalog products.
// This stays very lightweight so the UI can keep things simple: a name
// to show in the selector and an optional image specific to that color.
export type CatalogProductVariantSize = {
	name: string;
	inventory: number | string;
};

export type CatalogProductColorVariant = {
	id: string | number;
	name: string;
	// Optional full product photo specific to the color. We swap this in the
	// expanded card so the experience mirrors the InkSoft designer.
	imageSrc?: string;
	// Optional swatch hex used to tint the dropdown preview. We intentionally
	// keep this a simple string (e.g. "#000000") so the UI can fall back to
	// neutral colors when InkSoft does not provide a value.
	swatchHex?: string;
	// Optional swatch image (some InkSoft products expose tiny fabric previews).
	// Having the field documented here makes it easy to use later without
	// touching the InkSoft fetch logic again.
	swatchImageSrc?: string;
	sizes?: CatalogProductVariantSize[];
};

export type CatalogProduct = {
	// InkSoft Product ID used when launching the embedded designer.
	// This should match the numeric ID you see in InkSoft's product admin,
	// not the SKU / style code printed on the tag.
	id?: string;
	name: string;
	imageSrc?: string;
	description?: string;
	categorySlug: string;
	// Optional extended metadata when we have it from InkSoft.
	manufacturer?: string;
	sku?: string;
	// Optional list of color variants for this product. When present, the
	// Products page overlay can render a color picker and swap images as
	// different colors are selected.
	colorVariants?: CatalogProductColorVariant[];
};

export const categories: Category[] = [
	{
		name: "Basic tees",
		slug: "essentials",
		description: "Everyday must-haves for your team.",
		inksoftCategoryPath: "Ampbrand.com > Basic tees",
	},
	{
		// Display label is "Headwear" on the site; InkSoft category path stays
		// "Hats" so product fetches still match the store taxonomy.
		name: "Headwear",
		slug: "hats",
		description: "Caps, beanies and premium headwear.",
		inksoftCategoryPath: "Ampbrand.com > Hats",
	},
	{
		name: "Polos",
		slug: "polos",
		description: "Clean polos for every team.",
		inksoftCategoryPath: "Ampbrand.com > Polos",
	},
	{
		name: "Active wear",
		slug: "active-wear",
		description: "Performance gear for training and events.",
		inksoftCategoryPath: "Ampbrand.com > Active Wear",
	},
	{
		name: "Womens",
		slug: "womens",
		description: "Curated fits and styles for women.",
		inksoftCategoryPath: "Ampbrand.com > Womens",
	},
	{
		name: "Hoodies",
		slug: "hoodies",
		description: "Comfortable and stylish hoodies for any occasion.",
		inksoftCategoryPath: "Ampbrand.com > Hoodies and Sweatshirts",
	},
	{
		// Display label is "Drinkwear". InkSoft category name is Drinkwear,
		// so the nested path below is what the products fetch matches.
		// Placed before Work wear so "We have too much" stays the last section
		// and the subnav can still pin Work wear just ahead of it.
		name: "Drinkwear",
		slug: "drinkwear",
		description: "Tumblers and drinkwear for your team.",
		inksoftCategoryPath: "Ampbrand.com > Drinkwear",
	},
	{
		name: "Work wear",
		slug: "work-wear",
		description: "Durable apparel made for the job.",
		inksoftCategoryPath: "Ampbrand.com > Work wear",
	},
	{
		name: "We have too much",
		slug: "we-have-too-much",
		description: "Discounted items and overstock deals.",
		inksoftCategoryPath: "Ampbrand.com > We have too much",
	},
];


